import React, { useEffect, useState } from "react";
import { KeyRound, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageTitle, Field, TextInput } from "./shared";

const TOGGLES = [
  { key: "send_customer_confirmation", label: "CUSTOMER ORDER CONFIRMATION" },
  { key: "notify_new_order", label: "EMAIL ME ON NEW ORDERS" },
  { key: "notify_new_enquiry", label: "EMAIL ME ON NEW ENQUIRIES" },
  { key: "send_shipped", label: "CUSTOMER SHIPPED NOTIFICATION" },
];

const NotificationSettings = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/admin/notification-settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const save = async () => {
    await api.put("/admin/notification-settings", settings);
    toast.success("Notification settings saved");
  };

  if (!settings) return null;

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 space-y-4" data-testid="notification-settings">
      <h2 className="font-serif-display uppercase text-plum-deep text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-magenta" /> EMAIL NOTIFICATIONS</h2>
      <Field label="SEND ADMIN ALERTS TO">
        <TextInput type="email" value={settings.admin_email || ""} onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })} data-testid="notification-admin-email" />
      </Field>
      <div className="space-y-2">
        {TOGGLES.map((t) => (
          <label key={t.key} className="flex items-center gap-3 bg-cream rounded-xl px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings[t.key] !== false}
              onChange={() => setSettings({ ...settings, [t.key]: settings[t.key] === false })}
              data-testid={`notification-toggle-${t.key}`}
              className="w-4 h-4 accent-[#EC2675]"
            />
            <span className="text-sm">{t.label}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-plum-deep/50">Until you verify your own domain in Resend, emails only deliver to your Resend account email. Verify thebloomfoundry.nz at resend.com/domains to email customers directly.</p>
      <button onClick={save} className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum" data-testid="save-notification-settings">
        <Save className="w-4 h-4" /> SAVE NOTIFICATIONS
      </button>
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="console-settings">
      <PageTitle kicker="ACCOUNT" title="SETTINGS" />
      <div className="max-w-md space-y-6">
        <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6">
          <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-2">SIGNED IN AS</h2>
          <p className="text-sm">{user?.name}</p>
          <p className="text-sm text-plum-deep/60">{user?.email}</p>
        </div>

        <NotificationSettings />

        <form onSubmit={submit} className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 space-y-4" data-testid="change-password-form">
          <h2 className="font-serif-display uppercase text-plum-deep text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-magenta" /> CHANGE PASSWORD</h2>
          <Field label="CURRENT PASSWORD">
            <TextInput type="password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="settings-current-password" />
          </Field>
          <Field label="NEW PASSWORD (MIN 8 CHARACTERS)">
            <TextInput type="password" required minLength={8} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} data-testid="settings-new-password" />
          </Field>
          <Field label="CONFIRM NEW PASSWORD">
            <TextInput type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="settings-confirm-password" />
          </Field>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum disabled:opacity-50" data-testid="settings-save-password">
            <Save className="w-4 h-4" /> {busy ? "SAVING…" : "UPDATE PASSWORD"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
